import React, { useEffect, useState } from 'react';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import {
  CustomFieldValidator,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';
import { FormControl, TextField, Typography, makeStyles } from '@material-ui/core';
import CheckCircle from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';

/**
 * App name field for the app-bootstrap templates (`ui:field: AppNamePicker`).
 *
 * App names must be unique across ALL business units: every app gets
 * <name>.sparrowapps.com and <name>-staging.sparrowapps.com in one shared DNS
 * zone. As the user types, the field asks the internal backend
 * (GET /requests/app-bootstrap/name-check) whether the name is free, checking the
 * Service Catalog, open requests, DNS and the reserved list, and shows the
 * answer under the field. The same check runs as the form's validation, so
 * "Next" is blocked on a taken name. The Register step re-checks and reserves
 * the name server-side, so this field is the convenience and not the guard.
 */

type CheckResult = {
  name: string;
  available: boolean;
  reasons: { source: string; message: string }[];
  warnings: string[];
};

async function checkName(
  name: string,
  discovery: { getBaseUrl(id: string): Promise<string> },
  fetchApi: { fetch: typeof fetch },
): Promise<CheckResult> {
  const base = await discovery.getBaseUrl('internal');
  const res = await fetchApi.fetch(
    `${base}/requests/app-bootstrap/name-check?name=${encodeURIComponent(name)}`,
  );
  if (!res.ok) throw new Error(`name check failed (HTTP ${res.status})`);
  return res.json();
}

const DEBOUNCE_MS = 400;

const useStyles = makeStyles(theme => ({
  status: { display: 'inline-flex', alignItems: 'center', gap: theme.spacing(0.5) },
  ok: { color: theme.palette.success.main },
  icon: { fontSize: 14 },
}));

export const AppNamePicker = (props: FieldExtensionComponentProps<string>) => {
  const { onChange, rawErrors, required, formData, schema, idSchema } = props;
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const classes = useStyles();
  const [status, setStatus] = useState<
    { state: 'idle' } | { state: 'checking' } | { state: 'done'; result: CheckResult } | { state: 'error' }
  >({ state: 'idle' });

  useEffect(() => {
    const name = (formData ?? '').trim();
    if (!name) {
      setStatus({ state: 'idle' });
      return undefined;
    }
    let cancelled = false;
    setStatus({ state: 'checking' });
    const timer = setTimeout(() => {
      checkName(name, discovery, fetchApi)
        .then(result => !cancelled && setStatus({ state: 'done', result }))
        .catch(() => !cancelled && setStatus({ state: 'error' }));
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [formData, discovery, fetchApi]);

  let helper: React.ReactNode = schema.description;
  if (status.state === 'checking') {
    helper = 'Checking availability…';
  } else if (status.state === 'error') {
    helper = 'Could not check availability right now. It will be checked again on submit.';
  } else if (status.state === 'done' && status.result.available) {
    helper = (
      <Typography component="span" variant="caption" className={`${classes.status} ${classes.ok}`}>
        <CheckCircle className={classes.icon} /> Available: {status.result.name}.sparrowapps.com
      </Typography>
    );
  } else if (status.state === 'done') {
    helper = (
      <Typography component="span" variant="caption" color="error" className={classes.status}>
        <ErrorIcon className={classes.icon} /> {status.result.reasons.map(r => r.message).join(' · ')}
      </Typography>
    );
  }

  const taken = status.state === 'done' && !status.result.available;
  return (
    <FormControl margin="normal" required={required} fullWidth>
      <TextField
        id={idSchema?.$id}
        label={schema.title ?? 'Name'}
        required={required}
        value={formData ?? ''}
        onChange={e => onChange(e.target.value.trim().toLowerCase())}
        error={taken || Boolean(rawErrors?.length)}
        helperText={helper}
        variant="outlined"
        inputProps={{ autoComplete: 'off', spellCheck: false }}
      />
    </FormControl>
  );
};

/** Blocks "Next" / "Create" while the name is taken (async scaffolder validation). */
export const appNamePickerValidation: CustomFieldValidator<string> = async (
  value,
  validation,
  context,
) => {
  const name = (value ?? '').trim();
  if (!name) return;
  const discovery = context.apiHolder.get(discoveryApiRef);
  const fetchApi = context.apiHolder.get(fetchApiRef);
  if (!discovery || !fetchApi) return;
  try {
    const result = await checkName(name, discovery, fetchApi);
    if (!result.available) {
      validation.addError(result.reasons.map(r => r.message).join(' · '));
    }
  } catch {
    // Backend unreachable: let the Register step (server-side check) decide.
  }
};
